// src/diagrams/reactFlowContactFlowService.js - React Flow service with data lookup

import { ContactFlowReactFlowParser } from './contactFlowReactFlowParser';

export class ReactFlowContactFlowService {
  constructor() {
    this.validationRules = new ContactFlowValidationRules();
  }

  // Generate diagram data for React Flow with data lookup
  async generateDiagrams(exportedData, instanceAlias = 'Unknown', options = {}) {
    const defaultOptions = {
      includeMetadata: true,
      validateFlows: true,
      tooltips: true,
      skipDefaultFlows: true,
      skipEmptyFlows: true,
      generateSummary: true,
      onProgress: null
    };

    const finalOptions = { ...defaultOptions, ...options };
    const processedFlows = [];
    const timestamp = new Date().toISOString().split('T')[0];
    const summary = {
      processed: 0,
      generated: 0,
      skipped: 0,
      errors: 0,
      validationResults: []
    };
    
    console.log('Starting React Flow contact flow and module processing...');
    
    // Process each instance
    for (const [instanceId, instanceData] of Object.entries(exportedData)) {
      // Process Contact Flows
      if (instanceData.contactFlows && Array.isArray(instanceData.contactFlows)) {
        console.log(`Processing ${instanceData.contactFlows.length} contact flows for instance ${instanceId}`);
        
        let processedCount = 0;
        
        for (const contactFlow of instanceData.contactFlows) {
          try {
            summary.processed++;
            processedCount++;
            
            if (finalOptions.onProgress) {
              finalOptions.onProgress({
                instanceId,
                processed: processedCount,
                total: instanceData.contactFlows.length + (instanceData.contactFlowModules?.length || 0),
                flowName: contactFlow.Name,
                phase: 'processing',
                type: 'flow'
              });
            }
            
            const result = await this.processSingleFlow(
              contactFlow,
              instanceAlias,
              timestamp,
              finalOptions,
              exportedData,
              instanceId
            );
            
            if (result) {
              result.resourceType = 'flow'; // Add type identifier
              processedFlows.push(result);
              summary.generated++;
              
              if (finalOptions.validateFlows && result.validation) {
                summary.validationResults.push({
                  flowName: contactFlow.Name,
                  flowId: contactFlow.Id,
                  type: 'flow',
                  ...result.validation
                });
              }
            } else {
              summary.skipped++;
            }
            
          } catch (error) {
            console.error(`Failed to process flow ${contactFlow.Name}:`, error);
            summary.errors++;
            
            processedFlows.push({
              flowName: contactFlow.Name,
              flowId: contactFlow.Id,
              resourceType: 'flow',
              error: error.message,
              processed: false
            });
          }
        }
      }

      // Process Contact Flow Modules
      if (instanceData.contactFlowModules && Array.isArray(instanceData.contactFlowModules)) {
        console.log(`Processing ${instanceData.contactFlowModules.length} contact flow modules for instance ${instanceId}`);
        
        let processedCount = (instanceData.contactFlows?.length || 0);
        
        for (const contactFlowModule of instanceData.contactFlowModules) {
          try {
            summary.processed++;
            processedCount++;
            
            if (finalOptions.onProgress) {
              finalOptions.onProgress({
                instanceId,
                processed: processedCount,
                total: (instanceData.contactFlows?.length || 0) + instanceData.contactFlowModules.length,
                flowName: contactFlowModule.Name,
                phase: 'processing',
                type: 'module'
              });
            }
            
            const result = await this.processSingleModule(
              contactFlowModule,
              instanceAlias,
              timestamp,
              finalOptions,
              exportedData,
              instanceId
            );
            
            if (result) {
              result.resourceType = 'module'; // Add type identifier
              processedFlows.push(result);
              summary.generated++;
              
              if (finalOptions.validateFlows && result.validation) {
                summary.validationResults.push({
                  flowName: contactFlowModule.Name,
                  flowId: contactFlowModule.Id,
                  type: 'module',
                  ...result.validation
                });
              }
            } else {
              summary.skipped++;
            }
            
          } catch (error) {
            console.error(`Failed to process module ${contactFlowModule.Name}:`, error);
            summary.errors++;
            
            processedFlows.push({
              flowName: contactFlowModule.Name,
              flowId: contactFlowModule.Id,
              resourceType: 'module',
              error: error.message,
              processed: false
            });
          }
        }
      }
    }
    
    console.log(`React Flow processing complete: ${summary.generated} processed, ${summary.skipped} skipped, ${summary.errors} errors`);
    
    return {
      flows: processedFlows,
      summary,
      exportedData
    };
  }

  // Process single contact flow with data lookup
  async processSingleFlow(contactFlow, instanceAlias, timestamp, options, exportedData = null, instanceId = null) {
    // Pre-flight checks
    if (!this.shouldProcessFlow(contactFlow, options)) {
      return null;
    }

    try {
      // Validate flow if requested
      let validation = null;
      if (options.validateFlows) {
        validation = this.validationRules.validateFlow(contactFlow);
      }
      
      // Parse the contact flow content with data lookup
      const parser = new ContactFlowReactFlowParser(
        contactFlow.Content, 
        exportedData,  // Pass exported data for lookups
        instanceId     // Pass instance ID for lookups
      );
      
      // Check if flow has content
      const metadata = parser.getFlowSummary();
      if (metadata.actionCount === 0) {
        return null;
      }
      
      // Convert to React Flow format (now includes data)
      const { nodes, edges } = parser.convertToReactFlow();
      
      // Build result object with metadata
      const flow = {
        flowName: contactFlow.Name,
        flowId: contactFlow.Id,
        flowType: contactFlow.ContactFlowType,
        instanceAlias: instanceAlias,
        instanceId: instanceId,
        nodes,
        edges,
        metadata: {
          ...metadata,
          flowState: contactFlow.ContactFlowState,
          flowStatus: contactFlow.ContactFlowStatus,
          description: contactFlow.Description,
          tags: contactFlow.Tags,
          hasDataLookup: !!(exportedData && instanceId) // Flag indicating data lookup is available
        },
        validation,
        processed: true,
        platform: 'reactflow',
        processedAt: new Date().toISOString(),
        originalFlow: contactFlow, // Include original for reference
        features: {
          entryNode: true,
          detailedNodes: true,
          conditions: true,
          dataLookup: !!(exportedData && instanceId)
        }
      };
      
      return flow;
      
    } catch (error) {
      console.error(`Error processing flow ${contactFlow.Name}:`, error);
      throw error;
    }
  }

  async processSingleModule(contactFlowModule, instanceAlias, timestamp, options, exportedData = null, instanceId = null) {
    // Pre-flight checks - similar to flows but for modules
    if (!this.shouldProcessModule(contactFlowModule, options)) {
      return null;
    }

    try {
      // Validate module if requested
      let validation = null;
      if (options.validateFlows) {
        validation = this.validationRules.validateFlow(contactFlowModule);
      }
      
      // Parse the contact flow module content with data lookup
      const parser = new ContactFlowReactFlowParser(
        contactFlowModule.Content, 
        exportedData,
        instanceId
      );
      
      // Check if module has content
      const metadata = parser.getFlowSummary();
      if (metadata.actionCount === 0) {
        return null;
      }
      
      // Convert to React Flow format
      const { nodes, edges } = parser.convertToReactFlow();
      
      // Build result object with metadata
      const module = {
        flowName: contactFlowModule.Name,
        flowId: contactFlowModule.Id,
        flowType: 'ContactFlowModule',
        instanceAlias: instanceAlias,
        instanceId: instanceId,
        nodes,
        edges,
        metadata: {
          ...metadata,
          moduleState: contactFlowModule.ContactFlowModuleState,
          moduleStatus: contactFlowModule.ContactFlowModuleStatus,
          description: contactFlowModule.Description,
          hasDataLookup: !!(exportedData && instanceId)
        },
        validation,
        processed: true,
        platform: 'reactflow',
        processedAt: new Date().toISOString(),
        originalFlow: contactFlowModule,
        features: {
          entryNode: true,
          detailedNodes: true,
          conditions: true,
          dataLookup: !!(exportedData && instanceId)
        }
      };
      
      return module;
      
    } catch (error) {
      console.error(`Error processing module ${contactFlowModule.Name}:`, error);
      throw error;
    }
  }

  // Check if flow should be processed (same logic as original)
  shouldProcessFlow(contactFlow, options) {
    if (options.skipDefaultFlows && contactFlow.IsDefault) {
      return false;
    }

    if (!contactFlow.Content) {
      return false;
    }

    if (options.skipEmptyFlows) {
      try {
        const parsed = typeof contactFlow.Content === 'string' 
          ? JSON.parse(contactFlow.Content) 
          : contactFlow.Content;
        
        if (!parsed.Actions || parsed.Actions.length === 0) {
          return false;
        }
      } catch (error) {
        return false;
      }
    }

    return true;
  }

  shouldProcessModule(contactFlowModule, options) {
    if (!contactFlowModule.Content) {
      return false;
    }

    if (options.skipEmptyFlows) {
      try {
        const parsed = typeof contactFlowModule.Content === 'string' 
          ? JSON.parse(contactFlowModule.Content) 
          : contactFlowModule.Content;
        
        if (!parsed.Actions || parsed.Actions.length === 0) {
          return false;
        }
      } catch (error) {
        return false;
      }
    }

    return true;
  }

  // Diagram summary with detailed analysis
  getDiagramSummary(exportedData, options = {}) {
    const summary = {
      totalFlows: 0,
      diagramableFlows: 0,
      defaultFlows: 0,
      flowsWithoutContent: 0,
      flowsByType: {},
      flowsByInstance: {},
      features: {
        entryNodeSupport: true,
        detailedNodeContent: true,
        conditionLabels: true,
        dataLookupCapability: true
      },
      complexityAnalysis: {
        simple: 0,
        moderate: 0,
        complex: 0,
        averageActions: 0
      },
      nodeTypeAnalysis: {},
      coverage: {
        messageNodes: 0,
        inputNodes: 0,
        compareNodes: 0,
        transferNodes: 0,
        lambdaNodes: 0,
        queueNodes: 0,
        hoursNodes: 0
      }
    };
    
    let totalActions = 0;
    
    for (const [instanceId, instanceData] of Object.entries(exportedData)) {
      if (!instanceData.contactFlows || !Array.isArray(instanceData.contactFlows)) {
        continue;
      }

      summary.flowsByInstance[instanceId] = {
        total: instanceData.contactFlows.length,
        diagramable: 0,
        default: 0,
        withoutContent: 0,
        complexity: { simple: 0, moderate: 0, complex: 0 },
        coverage: {
          messageNodes: 0,
          inputNodes: 0,
          compareNodes: 0,
          transferNodes: 0,
          lambdaNodes: 0,
          queueNodes: 0,
          hoursNodes: 0
        }
      };
      
      for (const flow of instanceData.contactFlows) {
        summary.totalFlows++;
        summary.flowsByInstance[instanceId].total++;
        
        const flowType = flow.ContactFlowType || 'Unknown';
        summary.flowsByType[flowType] = (summary.flowsByType[flowType] || 0) + 1;
        
        let actionCount = 0;
        let nodeTypeCounts = {};
        
        if (flow.Content && !flow.IsDefault) {
          try {
            const parsed = typeof flow.Content === 'string' ? JSON.parse(flow.Content) : flow.Content;
            if (parsed.Actions && Array.isArray(parsed.Actions)) {
              actionCount = parsed.Actions.length;
              totalActions += actionCount;
              
              // Analyze node types for coverage
              parsed.Actions.forEach(action => {
                const type = action.Type;
                nodeTypeCounts[type] = (nodeTypeCounts[type] || 0) + 1;
                summary.nodeTypeAnalysis[type] = (summary.nodeTypeAnalysis[type] || 0) + 1;
                
                // Track coverage
                if (type === 'MessageParticipant') {
                  summary.coverage.messageNodes++;
                  summary.flowsByInstance[instanceId].coverage.messageNodes++;
                }
                if (type === 'GetParticipantInput') {
                  summary.coverage.inputNodes++;
                  summary.flowsByInstance[instanceId].coverage.inputNodes++;
                }
                if (type === 'Compare') {
                  summary.coverage.compareNodes++;
                  summary.flowsByInstance[instanceId].coverage.compareNodes++;
                }
                if (type === 'TransferToFlow') {
                  summary.coverage.transferNodes++;
                  summary.flowsByInstance[instanceId].coverage.transferNodes++;
                }
                if (type === 'InvokeLambdaFunction') {
                  summary.coverage.lambdaNodes++;
                  summary.flowsByInstance[instanceId].coverage.lambdaNodes++;
                }
                if (type === 'UpdateContactTargetQueue') {
                  summary.coverage.queueNodes++;
                  summary.flowsByInstance[instanceId].coverage.queueNodes++;
                }
                if (type === 'CheckHoursOfOperation') {
                  summary.coverage.hoursNodes++;
                  summary.flowsByInstance[instanceId].coverage.hoursNodes++;
                }
              });
            }
          } catch (error) {
            // Invalid JSON, treat as no content
          }
        }
        
        if (actionCount > 0) {
          if (actionCount <= 5) {
            summary.complexityAnalysis.simple++;
            summary.flowsByInstance[instanceId].complexity.simple++;
          } else if (actionCount <= 15) {
            summary.complexityAnalysis.moderate++;
            summary.flowsByInstance[instanceId].complexity.moderate++;
          } else {
            summary.complexityAnalysis.complex++;
            summary.flowsByInstance[instanceId].complexity.complex++;
          }
        }
        
        if (flow.IsDefault) {
          summary.defaultFlows++;
          summary.flowsByInstance[instanceId].default++;
        } else if (!flow.Content || actionCount === 0) {
          summary.flowsWithoutContent++;
          summary.flowsByInstance[instanceId].withoutContent++;
        } else {
          summary.diagramableFlows++;
          summary.flowsByInstance[instanceId].diagramable++;
        }
      }
    }
    
    const flowsWithContent = summary.totalFlows - summary.defaultFlows - summary.flowsWithoutContent;
    summary.complexityAnalysis.averageActions = flowsWithContent > 0 
      ? Math.round(totalActions / flowsWithContent) 
      : 0;
    
    return summary;
  }

  // Validate flow content (reuse existing validation)
  validateFlowContent(content) {
    return this.validationRules.validateFlowContent(content);
  }

  // Flow statistics with more detail
  getFlowStatistics(contactFlow, exportedData = null, instanceId = null) {
    if (!contactFlow.Content) {
      return { error: 'No content available' };
    }
    
    try {
      const parser = new ContactFlowReactFlowParser(contactFlow.Content, exportedData, instanceId);
      const metadata = parser.getFlowSummary();
      const actions = parser.getActions();
      const transitions = parser.getAllTransitions();
      
      // Statistics
      const nodeTypeBreakdown = {};
      const nodeCounts = {
        withEntryNode: true,
        messageNodesWithText: 0,
        inputNodesWithPrompts: 0,
        compareNodesWithConditions: 0,
        transferNodesWithTargets: 0,
        lambdaNodesWithFunctions: 0
      };
      
      actions.forEach(action => {
        const type = action.Type;
        nodeTypeBreakdown[type] = (nodeTypeBreakdown[type] || 0) + 1;
        
        // Count nodes
        if (type === 'MessageParticipant' && action.Parameters?.Text) {
          nodeCounts.messageNodesWithText++;
        }
        if (type === 'GetParticipantInput' && action.Parameters?.Text) {
          nodeCounts.inputNodesWithPrompts++;
        }
        if (type === 'Compare' && action.Parameters?.ComparisonValue) {
          nodeCounts.compareNodesWithConditions++;
        }
        if (type === 'TransferToFlow' && action.Parameters?.ContactFlowId) {
          nodeCounts.transferNodesWithTargets++;
        }
        if (type === 'InvokeLambdaFunction' && action.Parameters?.LambdaFunctionARN) {
          nodeCounts.lambdaNodesWithFunctions++;
        }
      });
      
      return {
        actionCount: metadata.actionCount,
        transitionCount: transitions.length,
        hasPositionData: metadata.hasPositionData,
        startAction: metadata.startAction,
        endActions: actions.filter(action => parser.isEndAction(action.Identifier)).length,
        nodeTypeBreakdown,
        nodeCounts,
        hasDataLookup: !!(exportedData && instanceId),
        validation: this.validationRules.validateFlow({ Content: contactFlow.Content })
      };
      
    } catch (error) {
      return { error: error.message };
    }
  }

  // Export processed flows data with information
  exportFlowsData(flows, format = 'json') {
    switch (format) {
      case 'json':
        return this.exportAsJSON(flows);
      case 'csv':
        return this.exportAsCSV(flows);
      default:
        throw new Error(`Unsupported export format: ${format}`);
    }
  }

  exportAsJSON(flows) {
    const exportData = flows.map(flow => ({
      name: flow.flowName,
      id: flow.flowId,
      type: flow.flowType,
      nodeCount: flow.nodes?.length || 0,
      edgeCount: flow.edges?.length || 0,
      complexity: this.calculateComplexity(flow),
      validation: flow.validation,
      metadata: flow.metadata,
      features: flow.features || {},
      hasDataLookup: flow.metadata?.hasDataLookup || false
    }));

    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `contact-flows-analysis-${new Date().toISOString().split('T')[0]}.json`;
    link.click();
    URL.revokeObjectURL(url);
  }

  exportAsCSV(flows) {
    const headers = [
      'Name', 'ID', 'Type', 'Nodes', 'Edges', 'Complexity', 'Valid', 'Errors', 'Warnings',
      'Has Entry Node', 'Has Data Lookup', 'Data Lookup Available'
    ];
    
    const rows = flows.map(flow => [
      flow.flowName,
      flow.flowId,
      flow.flowType,
      flow.nodes?.length || 0,
      flow.edges?.length || 0,
      this.calculateComplexity(flow),
      flow.validation?.isValid ? 'Yes' : 'No',
      flow.validation?.issues?.length || 0,
      flow.validation?.warnings?.length || 0,
      flow.features?.entryNode ? 'Yes' : 'No',
      flow.features?.detailedNodes ? 'Yes' : 'No',
      flow.features?.dataLookup ? 'Yes' : 'No'
    ]);

    const csvContent = [headers, ...rows]
      .map(row => row.map(cell => `"${cell}"`).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `contact-flows-analysis-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  }

  calculateComplexity(flow) {
    if (!flow.nodes || !flow.edges) return 'Unknown';
    
    const nodeCount = flow.nodes.length;
    const edgeCount = flow.edges.length;
    const score = nodeCount + (edgeCount * 0.5);
    
    if (score <= 10) return 'Simple';
    if (score <= 25) return 'Moderate';
    if (score <= 50) return 'Complex';
    return 'Very Complex';
  }
}

// Validation rules class
class ContactFlowValidationRules {
  validateFlow(contactFlow) {
    const issues = [];
    const warnings = [];
    
    try {
      const content = typeof contactFlow.Content === 'string' 
        ? JSON.parse(contactFlow.Content) 
        : contactFlow.Content;
      
      if (!content.StartAction) {
        issues.push('Missing start action');
      }
      
      if (!content.Actions || !Array.isArray(content.Actions)) {
        issues.push('Missing or invalid actions array');
      }
      
      // Validation checks
      if (content.Actions) {
        const actionIds = new Set();
        let hasMessageWithoutText = 0;
        let hasCompareWithoutValue = 0;
        let hasInputWithoutPrompt = 0;
        
        content.Actions.forEach(action => {
          if (!action.Identifier) {
            issues.push('Action missing identifier');
          } else if (actionIds.has(action.Identifier)) {
            issues.push(`Duplicate action identifier: ${action.Identifier}`);
          } else {
            actionIds.add(action.Identifier);
          }
          
          if (!action.Type) {
            issues.push(`Action ${action.Identifier} missing type`);
          }
          
          // Validation for specific node types
          if (action.Type === 'MessageParticipant' && !action.Parameters?.Text) {
            hasMessageWithoutText++;
          }
          
          if (action.Type === 'Compare' && !action.Parameters?.ComparisonValue) {
            hasCompareWithoutValue++;
          }
          
          if (action.Type === 'GetParticipantInput' && !action.Parameters?.Text) {
            hasInputWithoutPrompt++;
          }
        });
        
        // Add warnings for opportunities
        if (hasMessageWithoutText > 0) {
          warnings.push(`${hasMessageWithoutText} message nodes without text content`);
        }
        
        if (hasCompareWithoutValue > 0) {
          warnings.push(`${hasCompareWithoutValue} compare nodes without comparison values`);
        }
        
        if (hasInputWithoutPrompt > 0) {
          warnings.push(`${hasInputWithoutPrompt} input nodes without prompt text`);
        }
      }
      
      return {
        isValid: issues.length === 0,
        issues,
        warnings,
        score: Math.max(0, 100 - (issues.length * 20) - (warnings.length * 5)),
        opportunities: warnings.length
      };
      
    } catch (error) {
      return {
        isValid: false,
        issues: [`Invalid JSON structure: ${error.message}`],
        warnings: [],
        score: 0,
        opportunities: 0
      };
    }
  }

  validateFlowContent(content) {
    try {
      const parsed = typeof content === 'string' ? JSON.parse(content) : content;
      
      if (!parsed.Actions || !Array.isArray(parsed.Actions)) {
        return { valid: false, error: 'No actions found in flow' };
      }
      
      if (!parsed.StartAction) {
        return { valid: false, error: 'No start action defined' };
      }
      
      return { valid: true };
      
    } catch (error) {
      return { valid: false, error: `Invalid JSON: ${error.message}` };
    }
  }
}

// Export singleton instance
export const reactFlowContactFlowService = new ReactFlowContactFlowService();